import { SAFEGUARDING_ITEM_ID } from '@lmc/common/types'
import type {
  DetailsObject,
  FollowUp,
  Home,
  IncidentReport,
  IncidentSeverity
} from '@lmc/ts-common'
import { Form, FormCard } from '@lmc/ui-kit'
import { Form as AntdForm, type FormInstance } from 'antd'
import { showToast } from 'common/utils'
import useIncidentTypeOptions from 'hooks/useIncidentTypeOptions'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import useGoals from 'screens/Residents/screens/ResidentsGoalsScreen/components/useGoals/useGoals'
import styled from 'styled-components'
import {
  IconContainer,
  LogSubtitle,
  LogTitle
} from '../display/commonStyledComponents'
import type { AttachmentFieldType } from '../UploadAttachmentsOnSaveInput'
import useFollowUpActions from './hooks/useFollowUpActions'
import IncidentCaseFormFields from './IncidentCaseFormFields'
import IncidentLogFormFields, {
  type IncidentReportsLogFields
} from './IncidentLogFormFields'

const INCIDENT_CATEGORY_ITEM = '5e867702b349f776c8d48c34'

type IncidentCaseFormProps = {
  form: FormInstance
  onFinish: (values: IncidentReportCaseForm<string>) => void
  onValuesChange: (
    values:
      | Partial<IncidentReportEditCaseForm<string>>
      | Partial<IncidentReportCreateCaseForm<string>>
  ) => void
  initialValues: Partial<IncidentReportCaseForm<string>>
  incidentReport: IncidentReport
  testId?: string
}
const IncidentCaseForm = ({
  form,
  onFinish,
  onValuesChange,
  initialValues,
  incidentReport,
  testId = 'incident-case-form'
}: IncidentCaseFormProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)

  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )
  const { goals } = useGoals(incidentReport?.serviceUser.id)

  const notificationRequired = AntdForm.useWatch(
    ['followUpActions', 'notificationRequired'],
    form
  )
  const safeguardingRequired = AntdForm.useWatch(
    ['followUpActions', 'safeguardingActionsRequired'],
    form
  )

  const incidentTypes = useIncidentTypeOptions(INCIDENT_CATEGORY_ITEM)

  const isSafeguardingIncident =
    incidentReport.log.item?.id === SAFEGUARDING_ITEM_ID

  useFollowUpActions({
    form,
    notificationRequired,
    safeguardingRequired,
    initialValues,
    regulatorNotificationTitle: t(
      incidentReportsConstants.REQUIRES_REGULATOR_NOTIFICATION
    ),
    safeguardingTitle: t(incidentReportsConstants.REQUIRES_SAFEGUARDING)
  })

  // Derive initial yes/no status for each section from the underlying case data.
  // For create flows (no case yet), we default all sections to "no".
  const caseData = (incidentReport as any)?.case

  const hasAnyApplied = (keys: string[]): boolean => {
    if (!caseData) return false
    return keys.some((key) => Boolean(caseData?.[key]?.hasBeenApplied))
  }

  const derivedSectionStatuses = {
    restrictivePracticesStatus: hasAnyApplied([
      'physicalIntervention',
      'restraints',
      'chemicalRestraints',
      'environmentalRestraints',
      'verbalRestraints'
    ])
      ? 'yes'
      : 'no',
    injuriesStatus: hasAnyApplied([
      'residentInjuries',
      'carersInjuries',
      'otherPartiesInjuries'
    ])
      ? 'yes'
      : 'no',
    damagesStatus: hasAnyApplied(['damages', 'damagesIntroducingRisk'])
      ? 'yes'
      : 'no'
  } as const

  const formInitialValues: Partial<IncidentReportCaseForm<string>> = {
    ...initialValues,
    // Ensure the radio groups always reflect the underlying data on first render.
    restrictivePracticesStatus:
      derivedSectionStatuses.restrictivePracticesStatus,
    injuriesStatus: derivedSectionStatuses.injuriesStatus,
    damagesStatus: derivedSectionStatuses.damagesStatus
  }

  const handleValuesChange = (
    changedValues: Partial<IncidentReportCaseForm<string>>
  ) => {
    onValuesChange(changedValues)
  }

  return (
    <Form<IncidentReportCaseForm<string>>
      data-testid={`${testId}-form`}
      form={form}
      onFinish={onFinish}
      onValuesChange={handleValuesChange}
      initialValues={formInitialValues}
      onFinishFailed={({ errorFields }) =>
        errorFields.map((errorField) =>
          errorField.errors.map((err) =>
            showToast({ title: err, type: 'error' })
          )
        )
      }
    >
      <FormCard
        testId={`${testId}-log-card`}
        title={
          <RowContainer data-testid={`${testId}-title-container`}>
            <IconContainer
              data-testid={`${testId}-icon`}
              src={
                (incidentReport.log.item?.icon ||
                  incidentReport.log.category?.icon) as unknown as string
              }
              $color={incidentReport.log.category.color}
              alt="icon"
            />
            <ColumnContainer>
              <LogTitle level={3} data-testid={`${testId}-log-title`}>
                {incidentReport.log.title}
              </LogTitle>
              <LogSubtitle data-testid={`${testId}-logged-by`}>
                {t(incidentReportsConstants.LOGGED_BY)}{' '}
                {incidentReport.log.loggedBy?.name || 'carer'}
              </LogSubtitle>
            </ColumnContainer>
          </RowContainer>
        }
      >
        <IncidentLogFormFields
          testId={`${testId}-log-fields`}
          isCase
          goals={goals}
          locations={home?.locations}
          incidentTypes={incidentTypes}
          item={incidentReport.log.item}
          hasLocationsEnabled={
            home?.features?.hasHomeLocations?.enabled ?? false
          }
        />
      </FormCard>
      <IncidentCaseFormFields
        testId={`${testId}-case-fields`}
        form={form}
        severities={home?.incidentSeverity}
        residentId={incidentReport.serviceUser.id}
        isSafeguardingIncident={isSafeguardingIncident}
      />
    </Form>
  )
}

const RowContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`

const ColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
`

// fields included in both create/edit forms
export type IncidentReportCaseFormBase<T> = {
  serviceUsersInvolved: string[]
  // here you need to add the type from LogForm
  log: IncidentReportsLogFields<T>
  severity: null | IncidentSeverity
  otherPeopleInvolved: null | string
  physicalIntervention: DetailsObject
  restraints: DetailsObject
  chemicalRestraints: DetailsObject
  // new restrictive practice types
  environmentalRestraints: DetailsObject
  verbalRestraints: DetailsObject
  residentInjuries: DetailsObject
  carersInjuries: DetailsObject
  otherPartiesInjuries: DetailsObject
  damages: DetailsObject
  // damages introducing environmental risk
  damagesIntroducingRisk: DetailsObject
  prnMedication: DetailsObject
  otherClientsInvolved: [] | null
  otherWitnesses: string | null
  serviceUserOpinion: string | null
  incidentOtherInformation: string | null
  // textual explanations when no practices / injuries / damages occur
  noRestraintsDetails: string | null
  noInjuriesDetails: string | null
  noDamagesDetails: string | null
  // UI-only yes/no state for the three sections
  restrictivePracticesStatus?: 'yes' | 'no'
  injuriesStatus?: 'yes' | 'no'
  damagesStatus?: 'yes' | 'no'
  followUpActions: FollowUp
  debriefingNotes: string | null
  rootCause: string | null
  preventativeActions: string | null
  otherNotes: string | null
  attachments: AttachmentFieldType[]
  carersInvolved: string[]
}

// fields only for edit case
export interface IncidentReportEditCaseForm<T>
  extends IncidentReportCaseFormBase<T> {}

// fields only for create case
export interface IncidentReportCreateCaseForm<T>
  extends IncidentReportCaseFormBase<T> {}

// both edit/ create
export type IncidentReportCaseForm<T> =
  | IncidentReportEditCaseForm<T>
  | IncidentReportCreateCaseForm<T>

export default IncidentCaseForm
